import {
	RiArrowRightLine,
	RiBracesLine,
	RiFileCodeLine,
	RiInstanceLine,
	RiKey2Line,
	RiNodeTree,
	RiShieldKeyholeLine,
} from '@remixicon/react';
import { Fragment, type ReactNode } from 'react';

import { Backdrop } from '@shared/atoms/backdrop';
import { Hero } from '@shared/compositions/hero';
import type { RouteElement } from '@shared/router';
import * as history from '@shared/router/location';

import { Button } from '@playbook/atoms/button';
import { Card } from '@playbook/atoms/card';
import { Column, Container, Row } from '@playbook/modules/layout';

import * as routes from '@portal/pkg/routes';
import { PortalNavigation } from '@portal/views/_shared/navigation';
import { StepNumber } from '@portal/views/_shared/step-number';

import css from './styles.module.css';

interface Step {
	title: string;
	description: string;
}

const steps: Step[] = [
	{
		title: 'Get portal access',
		description:
			'An owner or admin of your organization grants your user access to the developer portal.',
	},
	{
		title: 'Create a key',
		description:
			'Choose what it may read and when it expires. Only the public half of the key pair ever reaches us.',
	},
	{
		title: 'Make a call',
		description:
			'Sign an assertion with your private key, trade it for a token and start reading.',
	},
];

interface Standard {
	icon: ReactNode;
	title: string;
	description: ReactNode;
}

function ExternalLink({
	href,
	children,
}: {
	href: string;
	children: ReactNode;
}): ReactNode {
	return (
		<a href={href} target="_blank" rel="noreferrer noopener">
			{children}
		</a>
	);
}

function toRfcUrl(rfc: number): string {
	return `https://www.rfc-editor.org/info/rfc${rfc}`;
}

const standards: Standard[] = [
	{
		icon: <RiShieldKeyholeLine />,
		title: 'OAuth 2.0 client credentials',
		description: (
			<Fragment>
				Tokens are issued per{' '}
				<ExternalLink href={toRfcUrl(6749)}>RFC 6749</ExternalLink>, for a
				signed <ExternalLink href={toRfcUrl(7519)}>JWT</ExternalLink> assertion
				per <ExternalLink href={toRfcUrl(7523)}>RFC 7523</ExternalLink>.
			</Fragment>
		),
	},
	{
		icon: <RiKey2Line />,
		title: 'ES256 and RS256 signatures',
		description:
			'Let your browser generate a P-256 key pair, or bring a P-256 or RSA key you already hold.',
	},
	{
		icon: <RiFileCodeLine />,
		title: 'OpenAPI 3.1',
		description: (
			<Fragment>
				The whole API is published as an{' '}
				<ExternalLink href="https://spec.openapis.org/oas/v3.1.0">
					OpenAPI
				</ExternalLink>{' '}
				document, ready for client generators and HTTP tools.
			</Fragment>
		),
	},
];

interface Feature {
	icon: ReactNode;
	title: string;
	description: string;
	href: string;
}

const features: Feature[] = [
	{
		icon: <RiKey2Line />,
		title: 'Secret. Safe. Secure.',
		description:
			'Expire, rotate and revoke keys whenever you need to. The private half never leaves your hands, and we never see it.',
		href: routes.Docs('keys'),
	},
	{
		icon: <RiNodeTree />,
		title: 'Scoped to a group',
		description:
			'Every token names one group, and reaches that group and every group below it. Easy access.',
		href: routes.Docs('groups-and-scopes'),
	},
	{
		icon: <RiBracesLine />,
		title: 'Any language. Same JSON.',
		description:
			'Plain HTTP and JSON, so any stack can call it. Every example comes in curl, Go, JavaScript and Java, with more to come.',
		href: routes.Reference(),
	},
];

export default function Landing(): RouteElement {
	const icon: ReactNode = <RiInstanceLine />;

	return (
		<Fragment>
			<Backdrop variant="ripple" />
			<Hero heading="Built by you" leading="Powered by 360Player." icon={icon}>
				<PortalNavigation variant="floating" />
			</Hero>

			<main className={css.content}>
				<Container>
					<Column spacing={24}>
						<Row columns={['1fr', '1fr', 'repeat(3, 1fr)']} spacing={24}>
							{features.map((feature) => (
								<FeatureCard key={feature.title} {...feature} />
							))}
						</Row>

						<Row columns={['1fr', 'repeat(2, 1fr)']} spacing={24}>
							<GetStarted />
							<Standards />
						</Row>
					</Column>
				</Container>
			</main>
		</Fragment>
	);
}

function GetStarted(): ReactNode {
	return (
		<Card>
			<div className={css.card}>
				<Column spacing={24}>
					<header className={css.heading}>
						<h2>Get started in three steps</h2>
						<p>From no access to your first response.</p>
					</header>

					<ol className={css.steps}>
						{steps.map((step, index) => (
							<li key={step.title}>
								<div className={css.step}>
									<h3>
										<StepNumber value={index + 1} />
										{step.title}
									</h3>
									<p>{step.description}</p>
								</div>
							</li>
						))}
					</ol>

					<div className={css.actions}>
						<Button
							variant="primary"
							label="Read the full guide"
							trailingIcon={<RiArrowRightLine />}
							onClick={() => history.push(routes.Docs('getting-started'))}
						/>
					</div>
				</Column>
			</div>
		</Card>
	);
}

function Standards(): ReactNode {
	return (
		<Card>
			<div className={css.card}>
				<Column spacing={24}>
					<header className={css.heading}>
						<h2>Built on established standards</h2>
						<p>Nothing proprietary to learn, and tooling you already have.</p>
					</header>

					<ul className={css.steps}>
						{standards.map((standard) => (
							<li key={standard.title}>
								<div className={css.step}>
									<h3 className={css.standard}>
										{standard.icon}
										{standard.title}
									</h3>
									<p>{standard.description}</p>
								</div>
							</li>
						))}
					</ul>

					<div className={css.actions}>
						<Button
							variant="secondary"
							label="Read about authentication"
							trailingIcon={<RiArrowRightLine />}
							onClick={() => history.push(routes.Docs('authentication'))}
						/>
					</div>
				</Column>
			</div>
		</Card>
	);
}

function FeatureCard({ icon, title, description, href }: Feature): ReactNode {
	return (
		<Card href={href}>
			<div className={css.card}>
				<Column spacing={12}>
					<figure role="img" className={css.featureIcon}>
						{icon}
					</figure>
					<h3>{title}</h3>
					<p className={css.description}>{description}</p>
				</Column>
			</div>
		</Card>
	);
}
